import { Button, Modal, Radio, RadioGroup } from '@cimpress/react-components'
import moment from 'moment-timezone'
import * as React from 'react'
import { withTranslation, WithTranslation } from 'react-i18next'
import { SnackbarController } from '../../../common/components/SnackbarController'
import { logError } from '../../../common/logger'
import { ChangeType, PickupCalendar } from '../../models'
import { VenloOnlyWarning } from './VenloOnlyWarning'
import * as WeeklyCalendarContext from './WeeklyCalendarContext'

interface Props extends WithTranslation {
  calendar: PickupCalendar
  startTime: moment.Moment
  tags: string[]
  onClose: () => void
}

interface State {
  changeType: ChangeType
  loading: boolean
}

class RemovePickupModal extends React.Component<Props, State> {
  public static contextType = WeeklyCalendarContext.Context

  public context: React.ContextType<typeof WeeklyCalendarContext.Context>

  constructor(props: Props) {
    super(props)
    this.state = { loading: false, changeType: 'current' }
  }

  public render() {
    const onRemove = async () => {
      this.setLoading(true)
      try {
        await this.context.removePickupEvent(
          this.props.startTime,
          this.props.calendar,
          this.state.changeType,
          this.props.tags
        )

        SnackbarController.show(
          <span>{this.getPickupRemovedSnackbarMessage()}</span>,
          'success'
        )
      } catch (e) {
        SnackbarController.show(
          <span>
            {this.props.t(
              'calendars.weekly.removePickupModal.pickupRemovingFailed'
            )}
          </span>,
          'danger'
        )
        logError('Error removing pickup', e)
        this.setLoading(false)
      }
    }
    const disableButtons = this.state.loading

    const footer = (
      <>
        <Button
          type="default"
          onClick={this.props.onClose}
          key="cancel-btn"
          disabled={disableButtons}
        >
          {this.props.t('common.cancel')}
        </Button>
        <Button
          type="primary"
          onClick={onRemove}
          key="save-btn"
          disabled={disableButtons}
        >
          {this.props.t('calendars.weekly.removePickupModal.remove')}
        </Button>
      </>
    )

    const setCurrent = (
      e: React.ChangeEvent<HTMLInputElement>,
      value: ChangeType
    ) => this.setChangeType(value)
    const allowClosingModal = !this.state.loading

    return (
      <Modal
        show={true}
        onRequestHide={this.props.onClose}
        closeButton={allowClosingModal}
        closeOnOutsideClick={allowClosingModal}
        title={this.props.t('calendars.weekly.removePickupModal.title')}
        footer={footer}
      >
        <VenloOnlyWarning />
        <div>
          {this.props.t('calendars.weekly.removePickupModal.confirmation', {
            date: this.props.startTime,
          })}
        </div>
        <RadioGroup
          onChange={setCurrent}
          defaultSelected={'current'}
          name="changeType"
        >
          <Radio
            label={this.props.t(
              'calendars.weekly.removePickupModal.forSingleDate',
              { date: this.props.startTime }
            )}
            value="current"
          />

          <Radio
            label={this.props.t(
              'calendars.weekly.removePickupModal.forWeekday',
              { date: this.props.startTime }
            )}
            value="futureDayOfWeek"
          />
          <Radio
            label={this.props.t(
              'calendars.weekly.removePickupModal.forAllFuture',
              { date: this.props.startTime }
            )}
            value="allFutureDays"
          />
        </RadioGroup>
      </Modal>
    )
  }

  private getPickupRemovedSnackbarMessage(): string {
    const date = this.props.startTime
    const calendarName = this.props.calendar.name

    switch (this.state.changeType) {
      case 'current':
        return this.props.t(
          'calendars.weekly.removePickupModal.pickupRemovedSingle',
          {
            date,
            calendarName,
          }
        )

      case 'futureDayOfWeek':
        return this.props.t(
          'calendars.weekly.removePickupModal.pickupRemovedWeekday',
          {
            date,
            calendarName,
          }
        )

      case 'allFutureDays':
        return this.props.t(
          'calendars.weekly.removePickupModal.pickupRemovedEveryday',
          {
            date,
            calendarName,
          }
        )

      default:
        throw new Error(`Unhandled switch case '${this.state.changeType}'`)
    }
  }

  private setChangeType(changeType: ChangeType) {
    this.setState({ changeType })
  }

  private setLoading(loading: boolean) {
    this.setState({ loading })
  }
}

export default withTranslation()(RemovePickupModal)
